import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { portfolioData } from '../mock';
import { SketchStar } from '../components/SketchElements';
import { Lightbox } from '../components/Lightbox';
import { Carousel } from '../components/Carousel';

const categoryMap = {
  'gaming': 'gaming',
  'social-media': 'socialMedia',
  'illustration': 'illustration',
  'branding': 'branding',
  'video': 'video',
};

// Reusable image tile — clean print, paper grain, no tape, natural aspect ratio
const ImageTile = ({ src, alt, eager = false, onClick }) => (
  <div
    className="relative overflow-hidden mb-3 md:mb-5 break-inside-avoid cursor-zoom-in group"
    style={{
      boxShadow:
        '0 18px 36px rgba(0,0,0,0.5), 0 4px 10px rgba(0,0,0,0.35)',
    }}
    onClick={onClick}
  >
    <img
      src={src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      className="w-full h-auto block transition-transform duration-700 ease-out group-hover:scale-[1.03]"
      style={{ filter: 'saturate(0.95) contrast(1.04)' }}
    />
    {/* Subtle paper grain to keep the zine feel without tape */}
    <div
      className="absolute inset-0 pointer-events-none opacity-20"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='3'/><feColorMatrix values='0 0 0 0 0.1  0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0.32 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        mixBlendMode: 'overlay',
      }}
    />
  </div>
);

// Hero tile — keeps natural aspect ratio (no crop), so the cover reads as it was designed
const HeroTile = ({ src, alt, onClick }) => (
  <div
    className="relative overflow-hidden cursor-zoom-in group"
    style={{
      boxShadow:
        '0 24px 48px rgba(0,0,0,0.55), 0 8px 18px rgba(0,0,0,0.4)',
    }}
    onClick={onClick}
  >
    <img
      src={src}
      alt={alt}
      loading="eager"
      className="w-full h-auto block transition-transform duration-700 ease-out group-hover:scale-[1.02]"
      style={{ filter: 'saturate(0.95) contrast(1.04)' }}
    />
    <div
      className="absolute inset-0 pointer-events-none opacity-20"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='3'/><feColorMatrix values='0 0 0 0 0.1  0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0.32 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        mixBlendMode: 'overlay',
      }}
    />
  </div>
);

export const ProjectDetailPage = () => {
  const { categoryId, projectSlug } = useParams();
  const projectsKey = categoryMap[categoryId];
  const projects = portfolioData.projects[projectsKey] || [];
  const project = projects.find(
    (p) => String(p.slug || p.id) === String(projectSlug)
  );
  const category = portfolioData.categories.find((c) => c.id === categoryId);

  // Lightbox state — null means closed
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!project) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="text-center">
          <p className="font-marker text-3xl text-white/60 uppercase mb-6">
            project not found
          </p>
          <Link
            to={`/gallery/${categoryId}`}
            className="punk-btn inline-flex"
            data-testid="back-to-gallery-link"
          >
            <ArrowLeft className="w-5 h-5" />
            back to gallery
          </Link>
        </div>
      </div>
    );
  }

  const gallery = project.gallery || [];
  const carousels = project.carousels || [];
  // All images for lightbox navigation: carousel images first, then gallery
  const carouselImages = carousels.flatMap((c) => c.images || []);
  const allImages = [...carouselImages, ...gallery].filter(Boolean);

  return (
    <div className="min-h-screen pt-24 pb-20 relative overflow-hidden">
      {/* Paper grain texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.12]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='280'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Scattered red notebook stars */}
      {[
        { top: '10%', right: '6%', size: 20, rot: 15, delay: 0.4 },
        { top: '46%', left: '3%', size: 16, rot: -10, delay: 0.6 },
        { top: '82%', right: '5%', size: 22, rot: 20, delay: 0.8 },
      ].map((s, i) => {
        const posStyle = {
          width: `${s.size}px`,
          height: `${s.size}px`,
          transform: `rotate(${s.rot}deg)`,
        };
        if (s.left) posStyle.left = s.left;
        if (s.right) posStyle.right = s.right;
        if (s.top) posStyle.top = s.top;
        return (
          <SketchStar
            key={i}
            className="absolute text-[color:var(--punk-red)]/80 hidden sm:block"
            style={posStyle}
            delay={s.delay}
          />
        );
      })}

      <div className="container mx-auto max-w-6xl px-6 relative z-10">
        {/* Back link */}
        <Link
          to={`/gallery/${categoryId}`}
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-10 group"
          data-testid="back-to-gallery-link"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-marker text-base uppercase tracking-wide">
            back to {category?.title?.toLowerCase() || 'gallery'}
          </span>
        </Link>

        {/* Header: category eyebrow + title + red marker underline */}
        <div className="mb-10 animate-fade-in">
          <p
            className="font-typewriter text-xs md:text-sm uppercase tracking-[0.35em] text-[color:var(--punk-red)] mb-4"
            data-testid="project-category-label"
          >
            {category?.title}
          </p>
          <div className="relative inline-block">
            <h1
              className="font-stencil text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white tracking-tight uppercase leading-[0.9]"
              data-testid="project-title"
            >
              {project.title}
            </h1>
            {/* Red marker underline — extends past the text on both sides */}
            <svg
              viewBox="0 0 400 26"
              preserveAspectRatio="none"
              className="absolute -left-[6%] -bottom-3 w-[112%] h-5 md:h-7 pointer-events-none"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2,14 C30,8 70,18 110,11 C150,5 195,18 240,10 C285,4 330,17 370,11 C385,9 395,12 398,11"
                stroke="var(--punk-red)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{
                  animation:
                    'draw-path 1s cubic-bezier(0.65, 0, 0.35, 1) 0.4s forwards',
                }}
              />
              <path
                d="M5,17 C32,12 68,21 108,14 C148,9 192,21 238,13 C284,8 328,20 368,14 C384,12 394,15 396,14"
                stroke="var(--punk-red)"
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0.65"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{
                  animation:
                    'draw-path 1s cubic-bezier(0.65, 0, 0.35, 1) 0.55s forwards',
                }}
              />
            </svg>
          </div>

          {project.tagline && (
            <p className="font-hand text-xl md:text-2xl text-white/80 mt-10 max-w-3xl leading-snug">
              {project.tagline}
            </p>
          )}
        </div>

        {/* Meta strip: role / year / tools */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14 animate-fade-in-delay">
          {project.role && (
            <div>
              <h4 className="font-typewriter text-[10px] uppercase tracking-[0.3em] text-white/55 mb-2">
                Role
              </h4>
              <p className="font-hand text-lg md:text-xl text-white/85">
                {project.role}
              </p>
            </div>
          )}
          {project.year && (
            <div>
              <h4 className="font-typewriter text-[10px] uppercase tracking-[0.3em] text-white/55 mb-2">
                Year
              </h4>
              <p className="font-hand text-lg md:text-xl text-white/85">
                {project.year}
              </p>
            </div>
          )}
          {project.tools && project.tools.length > 0 && (
            <div>
              <h4 className="font-typewriter text-[10px] uppercase tracking-[0.3em] text-white/55 mb-2">
                Tools
              </h4>
              <div className="flex flex-wrap gap-2">
                {project.tools.map((tool, idx) => (
                  <span
                    key={idx}
                    className="font-marker text-xs md:text-sm text-white px-3 py-1 border-2 border-white/40 uppercase tracking-wide"
                    style={{
                      transform: `rotate(${idx % 2 === 0 ? -1 : 1}deg)`,
                    }}
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Overview / about block */}
        {project.overview && (
          <div className="mb-16 animate-fade-in-delay max-w-3xl">
            <h2 className="font-stencil text-2xl md:text-3xl text-white uppercase tracking-wider mb-5">
              about the project
            </h2>
            <p
              className="font-sans text-base md:text-lg text-white/85 leading-relaxed"
              data-testid="project-overview"
            >
              {project.overview}
            </p>
          </div>
        )}

        {/* Carousels — for projects organized into themed groups (e.g., Instagram campaigns) */}
        {carousels.length > 0 && (
          <div className="space-y-14 md:space-y-16" data-testid="project-carousels">
            {carousels.map((c, cIdx) => {
              // Offset for lightbox indices: previous carousels
              const offset = carousels
                .slice(0, cIdx)
                .reduce((sum, prev) => sum + (prev.images?.length || 0), 0);
              return (
                <Carousel
                  key={`carousel-${cIdx}`}
                  title={c.title}
                  images={c.images || []}
                  onImageClick={(i) => setLightboxIndex(offset + i)}
                />
              );
            })}
          </div>
        )}

        {/* Gallery — masonry columns: uniform column widths, each frame matches image proportions */}
        {gallery.length > 0 && (
          <div
            className="columns-1 sm:columns-2 lg:columns-3 gap-3 md:gap-5"
            data-testid="project-gallery"
          >
            {gallery.map((src, idx) => (
              <ImageTile
                key={`gallery-${idx}`}
                src={src}
                alt={`${project.title} — frame ${idx + 1}`}
                onClick={() => setLightboxIndex(carouselImages.length + idx)}
              />
            ))}
          </div>
        )}

        {/* Back to gallery CTA at the bottom */}
        <div className="text-center mt-16">
          <Link
            to={`/gallery/${categoryId}`}
            className="punk-btn inline-flex"
            data-testid="bottom-back-to-gallery"
          >
            <ArrowLeft className="w-5 h-5" />
            back to {category?.title?.toLowerCase() || 'gallery'}
          </Link>
        </div>
      </div>

      {/* Lightbox — click any image to open */}
      <Lightbox
        images={allImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onChange={setLightboxIndex}
      />
    </div>
  );
};
